import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { SESSION_EXPIRED_KEY } from '../../utils/authToken';
import { parseApiError } from '../../utils/apiError';
import { FormPage, Field, PasswordInput } from '../../common/components/FormKit';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Пришли сюда, потому что сессия истекла — показываем пояснение один раз
  const [sessionExpired] = useState(() => sessionStorage.getItem(SESSION_EXPIRED_KEY) === '1');
  useEffect(() => {
    sessionStorage.removeItem(SESSION_EXPIRED_KEY);
  }, []);
  const navigate = useNavigate();
  const location = useLocation();
  // Пришли со страницы регистрации после подтверждения почты
  const justRegistered = (location.state as { registered?: boolean } | null)?.registered === true;
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    setSubmitting(true);

    try {
      const loggedIn = await login(email, password);
      if (loggedIn.mustChangePassword) {
        // Вход по временному паролю из письма — предлагаем сразу задать свой
        navigate('/change-password', { state: { forced: true } });
      } else {
        // Возвращаем туда, откуда пришли (например, на страницу турнира), иначе на главную
        const from = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
        navigate(from?.pathname ? `${from.pathname}${from.search ?? ''}` : '/');
      }
    } catch (err) {
      const apiError = parseApiError(err);
      // 429 — слишком много попыток: сервер сообщает, через сколько можно повторить
      setError(apiError.status === 429 && apiError.message
        ? apiError.message
        : 'Ошибка входа. Проверьте email и пароль.');
      setSubmitting(false);
    }
  };

  return (
    <FormPage title="Вход" subtitle="Войдите, чтобы подавать заявки на турниры JUDO40" variant="narrow">
      <form className="form-page__form" onSubmit={handleSubmit}>
        {sessionExpired && !error && (
          <div className="form-page__notice" role="status">Сессия истекла. Войдите заново.</div>
        )}
        {justRegistered && !error && (
          <div className="form-page__notice" role="status">Почта подтверждена, регистрация завершена. Теперь можно войти.</div>
        )}
        {error && <div className="form-page__alert" role="alert">{error}</div>}

        <div className="form-page__row form-page__row--single">
          <Field label="Email" htmlFor="login-email">
            <input
              id="login-email"
              className="form-page__input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              placeholder="name@example.com"
              required
            />
          </Field>
        </div>

        <div className="form-page__row form-page__row--single">
          <PasswordInput
            id="login-password"
            label="Пароль"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
          />
        </div>

        <div className="form-page__actions">
          <Link to="/forgot-password" className="form-page__login-link"><span>Забыли пароль?</span></Link>
          <button type="submit" className="btn form-page__submit" disabled={submitting}>
            {submitting ? 'Входим…' : 'Войти'}
          </button>
        </div>

        <p className="form-page__footer-text">
          Нет аккаунта? <Link to="/register" className="form-page__hint-link">Зарегистрироваться</Link>
        </p>
      </form>
    </FormPage>
  );
};

export default Login;
